type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

interface LoggerConfig {
  level: LogLevel;
  enableVerbose: boolean;
}

class Logger {
  private config: LoggerConfig;
  private readonly levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    silent: 4,
  };

  constructor() {
    const isDevelopment = import.meta.env.DEV;
    const envLevel = (import.meta.env.VITE_LOG_LEVEL || (isDevelopment ? 'info' : 'error')).toLowerCase() as LogLevel;
    const enableVerbose = import.meta.env.VITE_ENABLE_VERBOSE_LOGGING === 'true' && isDevelopment;

    this.config = {
      level: this.isValidLevel(envLevel) ? envLevel : (isDevelopment ? 'info' : 'error'),
      enableVerbose,
    };
  }

  private isValidLevel(level: string): level is LogLevel {
    return level in this.levels;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.levels[level] >= this.levels[this.config.level];
  }

  private sanitize(data: any): any {
    if (typeof data === 'string') {
      return this.sanitizeString(data);
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitize(item));
    }

    if (data && typeof data === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(data)) {
        const lowerKey = key.toLowerCase();

        if (lowerKey.includes('password') ||
            lowerKey.includes('secret') ||
            lowerKey.includes('token') ||
            lowerKey.includes('bearer') ||
            lowerKey.includes('authorization')) {
          sanitized[key] = '[REDACTED]';
        } else if (lowerKey.includes('apikey') || lowerKey.includes('api_key')) {
          sanitized[key] = this.maskApiKey(String(value));
        } else if (lowerKey.includes('email') && typeof value === 'string') {
          sanitized[key] = this.maskEmail(value);
        } else if ((lowerKey.includes('userid') || lowerKey.includes('user_id') || lowerKey === 'id') && typeof value === 'string' && this.isUUID(value)) {
          sanitized[key] = this.maskUUID(value);
        } else if (lowerKey.includes('url') && typeof value === 'string' && value.includes('supabase.co')) {
          sanitized[key] = this.maskSupabaseUrl(value);
        } else {
          sanitized[key] = this.sanitize(value);
        }
      }
      return sanitized;
    }

    return data;
  }

  private sanitizeString(str: string): string {
    str = str.replace(/Bearer\s+[^\s]+/gi, 'Bearer [REDACTED]');
    str = str.replace(/apikey[=:]\s*[^\s&]+/gi, 'apikey=[REDACTED]');
    str = str.replace(/token[=:]\s*[^\s&]+/gi, 'token=[REDACTED]');
    str = str.replace(/password[=:]\s*[^\s&]+/gi, 'password=[REDACTED]');
    str = str.replace(/[a-z0-9]{20,}/gi, (match) => {
      if (match.length > 30) return `${match.substring(0, 8)}...${match.substring(match.length - 4)}`;
      return match;
    });

    return str;
  }

  private maskApiKey(key: string): string {
    if (!key || key.length < 8) return '[REDACTED]';
    return `${key.substring(0, 8)}...[REDACTED]`;
  }

  private maskEmail(email: string): string {
    if (!email || !email.includes('@')) return email;
    const [local, domain] = email.split('@');
    if (local.length <= 3) return `${local[0]}***@${domain}`;
    return `${local.substring(0, 3)}***@${domain}`;
  }

  private maskSupabaseUrl(url: string): string {
    return url.replace(/https:\/\/[^.]+\.supabase\.co/, 'https://[PROJECT].supabase.co');
  }

  private isUUID(str: string): boolean {
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidPattern.test(str);
  }

  private maskUUID(uuid: string): string {
    const isDevelopment = import.meta.env.DEV;
    if (isDevelopment) {
      return `${uuid.substring(0, 8)}-****-****-****-************`;
    }
    return '[UUID-REDACTED]';
  }

  debug(message: string, data?: any): void {
    if (!this.shouldLog('debug')) return;

    if (data !== undefined) {
      console.log(`🔍 ${message}`, this.sanitize(data));
    } else {
      console.log(`🔍 ${message}`);
    }
  }

  info(message: string, data?: any): void {
    if (!this.shouldLog('info')) return;

    if (data !== undefined) {
      console.log(`ℹ️ ${message}`, this.sanitize(data));
    } else {
      console.log(`ℹ️ ${message}`);
    }
  }

  warn(message: string, data?: any): void {
    if (!this.shouldLog('warn')) return;

    if (data !== undefined) {
      console.warn(`⚠️ ${message}`, this.sanitize(data));
    } else {
      console.warn(`⚠️ ${message}`);
    }
  }

  error(message: string, error?: any): void {
    if (!this.shouldLog('error')) return;

    if (error !== undefined) {
      console.error(`❌ ${message}`, error instanceof Error ? error : this.sanitize(error));
    } else {
      console.error(`❌ ${message}`);
    }
  }

  verbose(message: string, data?: any): void {
    if (!this.config.enableVerbose) return;

    if (data !== undefined) {
      console.log(`🔬 [VERBOSE] ${message}`, this.sanitize(data));
    } else {
      console.log(`🔬 [VERBOSE] ${message}`);
    }
  }

  group(label: string): void {
    if (this.shouldLog('debug')) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.shouldLog('debug')) {
      console.groupEnd();
    }
  }
}

export const logger = new Logger();
